import { useState, useEffect, forwardRef, useRef } from "react";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoRounded from '@mui/icons-material/InfoRounded';
import EmojiEmotionsRoundedIcon from '@mui/icons-material/EmojiEmotionsRounded';
import AnimatedNumber from "./components/AnimatedNumber";
import EmojiPickerPopper from "./components/EmojiPickerPopper";
import SuggestionButton from "./components/SuggestionButton";
import "./styles.scss";

const BaseInput = forwardRef(({ value, onInput, label, placeholder, className, variant = 'light', size, fullWidth, autoScroll, onFocus, startAffix, endAffix, inputSize, fitContent = false, ...props }, ref) => {
    const hasValue = value !== undefined && value !== null && value.toString().trim().length > 0;
    const currentLength = value ? value.toString().length : 0;
    const isStartAffix = Boolean(props.start) && (startAffix || typeof props.start === 'string');
    const isEndAffix = Boolean(props.end) && (endAffix || typeof props.end === 'string');
    const sizeClass = ['medium', 'small', 'xsmall'].includes(inputSize) ? `input-size-${inputSize}` : '';
    const textareaRef = useRef(null);
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const emojiButtonRef = useRef(null);
    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

    const handleFocus = (e) => {
        if (autoScroll && containerRef.current) {
            // Scroll with a slight delay to ensure keyboard/layout is settled, or just immediately
            setTimeout(() => {
                containerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 100);
        }
        if (onFocus) {
            onFocus(e);
        }
    };

    // Create a ref callback that updates both internal and external refs
    const setTextareaRef = (element) => {
        textareaRef.current = element;
        // Update external ref if provided
        if (ref) {
            if (typeof ref === 'function') {
                ref(element);
            } else {
                ref.current = element;
            }
        }
        // Initialize height when element is mounted
        if (element && props.multiline) {
            // Set initial height to match normal input (32px content + 8px padding = 40px total);
            // `fitContent` starts from the textarea's own rows instead.
            element.style.height = fitContent ? '' : '32px';
            element.style.overflowY = 'hidden';
            // Adjust after DOM is ready
            setTimeout(() => {
                adjustTextareaHeight();
            }, 0);
        }
    };

    const adjustTextareaHeight = () => {
        if (props.multiline && textareaRef.current) {
            const textarea = textareaRef.current;

            // Save current scroll position
            const scrollTop = textarea.scrollTop;

            // Temporarily set height to auto to get the correct scrollHeight
            textarea.style.height = 'auto';

            // Get the scroll height (content height)
            const scrollHeight = textarea.scrollHeight;

            // Minimum height for textarea content (32px + 8px padding = 40px total, matching normal input);
            // `fitContent` follows the content exactly (pair it with `rows={1}`).
            const minHeight = fitContent ? 0 : 32;
            // Maximum height is 500px
            const maxHeight = 500;

            // Calculate new height, respecting min and max
            let newHeight = Math.max(scrollHeight, minHeight);

            if (newHeight > maxHeight) {
                newHeight = maxHeight;
                textarea.style.overflowY = 'auto';
            } else {
                textarea.style.overflowY = 'hidden';
            }

            // Apply the calculated height
            textarea.style.height = `${newHeight}px`;

            // Restore scroll position
            textarea.scrollTop = scrollTop;
        }
    };

    useEffect(() => {
        if (props.multiline) {
            // Use setTimeout to ensure DOM is fully rendered before adjusting
            const timer = setTimeout(() => {
                adjustTextareaHeight();
            }, 0);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, props.multiline]);

    // Also adjust when component mounts with existing value
    useEffect(() => {
        if (props.multiline && textareaRef.current && value) {
            const timer = setTimeout(() => {
                adjustTextareaHeight();
            }, 50);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The content height depends on the width: a textarea measured while hidden (display: none,
    // as in a tab not shown yet) or before its container resized wraps differently once shown,
    // so a new width measures it again. Height changes are ignored (they are this adjustment),
    // and the next frame keeps the observer out of a resize loop.
    useEffect(() => {
        const textarea = textareaRef.current;
        if (!props.multiline || !textarea || typeof ResizeObserver === 'undefined') return undefined;
        let width = 0;
        let frame = null;
        const observer = new ResizeObserver(([entry]) => {
            const next = entry.contentRect.width;
            if (next === width) return;
            width = next;
            if (!next) return;
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(adjustTextareaHeight);
        });
        observer.observe(textarea);
        return () => { observer.disconnect(); cancelAnimationFrame(frame); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.multiline]);

    const handleTextareaChange = (e) => {
        const newValue = e.target.value;
        onInput(newValue);
        // Adjust height after value change using requestAnimationFrame for smooth updates
        requestAnimationFrame(() => {
            adjustTextareaHeight();
        });
    };

    const handleClear = (e) => {
        e.stopPropagation();
        if (onInput) {
            onInput('');
            // Adjust height after clearing if it's a textarea
            if (props.multiline) {
                requestAnimationFrame(() => {
                    adjustTextareaHeight();
                });
            }
        }
    };

    const handleEmojiClick = (e) => {
        e.stopPropagation();
        setEmojiPickerOpen(!emojiPickerOpen);
    };

    const handleEmojiSelect = (emojiData) => {
        const emoji = emojiData.emoji;
        const currentValue = value || '';
        const inputElement = props.multiline ? textareaRef.current : inputRef.current;

        if (inputElement && onInput) {
            const start = inputElement.selectionStart || currentValue.length;
            const end = inputElement.selectionEnd || currentValue.length;
            const newValue = currentValue.slice(0, start) + emoji + currentValue.slice(end);

            onInput(newValue);

            // Restore cursor position after emoji insertion
            setTimeout(() => {
                const newCursorPos = start + emoji.length;
                inputElement.setSelectionRange(newCursorPos, newCursorPos);
                inputElement.focus();
            }, 0);
        } else if (onInput) {
            // Fallback: just append emoji if we can't get cursor position
            onInput(currentValue + emoji);
        }

        setEmojiPickerOpen(false);
    };

    // Create a ref callback that updates both internal and external refs for input
    const setInputRef = (element) => {
        inputRef.current = element;
        // Update external ref if provided
        if (ref) {
            if (typeof ref === 'function') {
                ref(element);
            } else {
                ref.current = element;
            }
        }
    };

    return (
        <div ref={containerRef} className={
            `CustomInput_container 
            ${props.disabled ? 'disabled' : ''}
            ${props.error ? 'error' : ''}
            ${hasValue ? 'has-value' : ''}
            ${className || ''}
            ${variant === 'large' ? 'large' : 'small'}
            ${fullWidth ? 'full-width' : ''}
            `} style={{ position: "relative" }}>

            {
                label && (
                    <span className="CustomInput_label">
                        {label}
                    </span>
                )
            }

            <div className={[
                'CustomInput_platform-input-base',
                props.multiline ? 'multiline' : '',
                hasValue ? 'has-value' : '',
                props.start ? 'has-start' : '',
                props.end ? 'has-end' : '',
                isStartAffix ? 'has-start-affix has-affix' : '',
                isEndAffix ? 'has-end-affix has-affix' : '',
                sizeClass,
            ].filter(Boolean).join(' ')}>
                {
                    props.start && (
                        <span className={[
                            'input-start-icon',
                            'input-icon',
                            isStartAffix ? 'input-affix' : '',
                            typeof (props.start) === 'string' ? 'start-text' : '',
                        ].filter(Boolean).join(' ')}>
                            {props.start}
                        </span>
                    )
                }

                {
                    props.multiline ? (
                        <textarea
                            ref={setTextareaRef}
                            fullWidth
                            label={label || ''}
                            placeholder={placeholder}
                            value={value || ''}
                            variant="filled"
                            onChange={handleTextareaChange}
                            onFocus={handleFocus}
                            className={`custom-input custom-input-${variant}`.trim()}
                            {...props}
                        />
                    ) : (
                        <input
                            ref={setInputRef}
                            fullWidth
                            label={label || ''}
                            placeholder={placeholder}
                            value={value || ''}
                            variant="filled"
                            onChange={(e) => onInput(e.target.value)}
                            onFocus={handleFocus}
                            className={`custom-input custom-input-${variant}`.trim()}
                            {...props}
                        />
                    )
                }

                {
                    props.emoji && (
                        <span
                            ref={emojiButtonRef}
                            className="input-emoji"
                            onClick={handleEmojiClick}
                            style={{ cursor: 'pointer' }}
                        >
                            <EmojiEmotionsRoundedIcon />
                        </span>
                    )
                }

                {hasValue && (
                    <span className="input-clear" onClick={handleClear}>
                        <CloseRoundedIcon />
                    </span>
                )}

                {
                    props.end && (
                        <span className={[
                            'input-end-icon',
                            'input-icon',
                            isEndAffix ? 'input-affix' : '',
                            typeof (props.end) === 'string' ? 'end-text' : '',
                        ].filter(Boolean).join(' ')}>
                            {props.end}
                        </span>
                    )
                }
            </div>

            {
                props.helper || size ? (
                    <div className="CustomInput_bottom">
                        {
                            <span className="CustomInput_helper">
                                {props.helper && <InfoRounded />}
                                <span>
                                    {props.helper || null}
                                </span>
                            </span>
                        }
                        {size && (
                            <div className="CustomInput_counterBox">
                                <AnimatedNumber value={currentLength} />/{size}
                            </div>
                        )}
                    </div>
                ) : null
            }

            {props.emoji && (
                <EmojiPickerPopper
                    open={emojiPickerOpen}
                    anchorEl={emojiButtonRef.current}
                    onEmojiClick={handleEmojiSelect}
                    onClose={() => setEmojiPickerOpen(false)}
                    preview={props.emojiPreview || !props.multiline}
                />
            )}

            {props.suggestions && props.suggestions.length > 0 && (
                <div className="input-suggestions">
                    {props.suggestions.map((suggestion, index) => (
                        <SuggestionButton
                            key={index}
                            suggestion={suggestion}
                            onClick={onInput}
                        />
                    ))}
                </div>
            )}
        </div>
    );
});

export default BaseInput;
